import PlatformBaseController from "./platform_base_controller";

// Handles Property (site)/date filters for the AI Crawler Reports workspace
export default class extends PlatformBaseController {
    static targets = [
        "filterForm",
        "filterSubmit",
        "logStartDate",
        "logEndDate"
    ];

    static values = {
        initDates: { type: Boolean, default: true }
    };
    connect() {
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;
        this.skipDefaultDates = false;
        this.bootstrapInitialDates();
    }

    filterFormTargetConnected(target) {
        this.bootstrapInitialDates(target);
    }

    // Today is still accumulating data, so it's never a selectable option —
    // scoped to this controller's own date targets only.
    logStartDateTargetConnected(target) {
        this.disableToday(target);
    }

    logEndDateTargetConnected(target) {
        this.disableToday(target);
    }

    disableToday(input) {
        const yesterday = this.addDays(this.startOfLocalDay(new Date()), -1);
        input.max = this.formatYMD(yesterday);
    }

    bootstrapInitialDates(form = null) {
        const forms   = this.allFilterForms(form);
        const primary = forms.find((scope) => scope?.querySelector?.('[name="logs_start_date"]')) || forms[0];
        if (!primary) return;

        if (!this.shouldInitializeDates(primary)) {
            const start = primary.querySelector?.('[name="logs_start_date"]')?.value || "";
            const end   = primary.querySelector?.('[name="logs_end_date"]')?.value   || "";
            this.initialDates = { startYMD: start, endYMD: end };
            return;
        }

        const { startYMD, endYMD } = this.ensureFilterDates({ form: primary });
        this.initialDates = { startYMD, endYMD };
    }

    allFilterForms(preferred = null) {
        const forms = [];
        const push = (candidate) => {
            if (candidate && !forms.includes(candidate)) forms.push(candidate);
        };

        push(preferred);
        if (this.hasFilterFormTarget) this.filterFormTargets.forEach(push);
        push(this.element.closest?.("form"));
        push(this.element);
        return forms;
    }

    shouldInitializeDates(form = null) {
        const datasetKey = this.datasetKeyFor("InitDatesValue");
        const sources    = [];

        if (form) sources.push(form.dataset?.[datasetKey]);
        if (this.hasFilterFormTarget) this.filterFormTargets.forEach((target) => sources.push(target.dataset?.[datasetKey]));
        sources.push(this.element.dataset?.[datasetKey]);

        for (const value of sources) {
            const parsed = this.parseBooleanDataset(value);
            if (parsed !== undefined) return parsed;
        }

        return this.initDatesValue;
    }

    parseBooleanDataset(value) {
        if (value === undefined || value === null || value === "") return undefined;
        if (typeof value === "boolean") return value;

        const normalised = String(value).toLowerCase();
        if (["false", "0", "no", "off"].includes(normalised)) return false;
        if (["true", "1", "yes", "on"].includes(normalised)) return true;
        return undefined;
    }

    datasetKeyFor(suffix) {
        return `${this.identifierCamelCase}${suffix}`;
    }

    get identifierCamelCase() {
        if (!this._identifierCamelCase) {
            this._identifierCamelCase = this.identifier.replace(/-([a-z])/g, (_m, chr) => chr.toUpperCase());
        }
        return this._identifierCamelCase;
    }

    setDates({ startYMD, endYMD }, form = null) {
        const forms = this.allFilterForms(form);

        forms.forEach((scope) => {
            if (!scope || typeof scope.querySelector !== "function") return;
            const startInput = scope.querySelector('[name="logs_start_date"]');
            const endInput   = scope.querySelector('[name="logs_end_date"]');

            if (startInput) startInput.value = startYMD != null ? startYMD : "";
            if (endInput)   endInput.value   = endYMD   != null ? endYMD   : "";
        });

        if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = startYMD != null ? startYMD : ""; });
        if (this.hasLogEndDateTarget)   this.logEndDateTargets.forEach((input) => { input.value   = endYMD   != null ? endYMD   : ""; });

        if (startYMD !== undefined) this.setPersistentFilter("ai_crawler_logs_start_date", startYMD != null ? startYMD : "");
        if (endYMD   !== undefined) this.setPersistentFilter("ai_crawler_logs_end_date",   endYMD   != null ? endYMD   : "");
    }

    ensureFilterDates({ event = null, form = null, period = 6 } = {}) {
        const fallbackForm =
            event?.target?.closest?.("form") ||
            event?.target?.form ||
            (this.hasFilterFormTarget ? this.filterFormTargets[0] : null) ||
            this.element.closest?.("form") ||
            this.element;

        const scopeForms = this.allFilterForms(form || fallbackForm);

        const today        = this.startOfLocalDay(new Date());
        const yesterday    = this.addDays(today, -1);
        const defaultEnd   = this.formatYMD(yesterday);
        const defaultStart = this.formatYMD(this.addDays(yesterday, -period));

        let resolvedStart = "";
        let resolvedEnd   = "";

        scopeForms.forEach((scope) => {
            if (!scope || typeof scope.querySelector !== "function") return;

            const startInput = scope.querySelector('[name="logs_start_date"]');
            const endInput   = scope.querySelector('[name="logs_end_date"]');

            if (startInput) {
                if (!startInput.value) startInput.value = defaultStart;
                if (!resolvedStart && startInput.value) resolvedStart = startInput.value;
            }

            if (endInput) {
                if (!endInput.value) endInput.value = defaultEnd;
                if (!resolvedEnd && endInput.value) resolvedEnd = endInput.value;
            }
        });

        if (resolvedStart) this.setPersistentFilter("ai_crawler_logs_start_date", resolvedStart);
        if (resolvedEnd)   this.setPersistentFilter("ai_crawler_logs_end_date",   resolvedEnd);

        if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = resolvedStart || ""; });
        if (this.hasLogEndDateTarget)   this.logEndDateTargets.forEach((input) => { input.value   = resolvedEnd   || ""; });

        return {
            startYMD: resolvedStart || "",
            endYMD:   resolvedEnd   || "",
            start:    resolvedStart ? this.toLocalDateOnly(resolvedStart) : undefined,
            end:      resolvedEnd ? this.toLocalDateOnly(resolvedEnd)   : undefined
        };
    }

    selectSite(event) {
        const site = event.target.value;
        this.setPersistentFilter("ai_crawler_site", site);
        this.updateSiteInputs(site);

        const form   = this.filterFormTargets[0] || this.element.closest?.("form") || this.element;
        const params = this.buildFilterParams(form);
        if (site) params.set("site", site);

        this.fetchAction(`/as_seen_by_ai/ai_crawler_reports/filter_parameters?${params.toString()}`);
    }

    updateSiteInputs(site) {
        this.allFilterForms().forEach((form) => {
            if (!form || typeof form.querySelector !== "function") return;
            const hidden = form.querySelector('[name="site"]');
            if (hidden) hidden.value = site;
        });
    }

    syncDateInputs(fieldName, value, exclude = null) {
        this.allFilterForms().forEach((form) => {
            if (!form || typeof form.querySelector !== "function") return;
            const input = form.querySelector(`[name="${fieldName}"]`);
            if (input && input !== exclude) input.value = value;
        });
    }

    removeFilterTag(event) {
        event.preventDefault();

        const clickedElement = event.currentTarget;
        const filterTag      = clickedElement.dataset.filterTagValue;

        if (filterTag) this.deletePersistentFilter(`ai_crawler_${filterTag}`);

        if (filterTag === "logs_start_date") {
            if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("logs_start_date", "");
            this.skipDefaultDates = true;
        }
        if (filterTag === "logs_end_date") {
            if (this.hasLogEndDateTarget) this.logEndDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("logs_end_date", "");
            this.skipDefaultDates = true;
        }
        clickedElement.remove();
    }

    clearFilters(event) {
        event.preventDefault();

        const form = event.target.closest("form") || event.target.form || this.element;
        const site = this.getPersistedFilter(form, "site");

        this.deletePersistentFilter("ai_crawler_logs_start_date");
        this.deletePersistentFilter("ai_crawler_logs_end_date");

        const today = this.startOfLocalDay(new Date());
        const yesterday = this.addDays(today, -1);
        const defaultEnd = this.formatYMD(yesterday);
        const defaultStart = this.formatYMD(this.addDays(yesterday, -6));
        const defaultDates = { startYMD: defaultStart, endYMD: defaultEnd };
        this.setDates(defaultDates, form);
        this.initialDates = defaultDates;
        this.skipDefaultDates = false;

        const params = new URLSearchParams();
        if (site) params.set("site", site);

        this.fetchAction(`/as_seen_by_ai/ai_crawler_reports/clear_filters?${params.toString()}`);
    }

    setFilters(event) {
        event.preventDefault();
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;

        const form  = event.target.closest?.("form") || event.target.form || this.element;
        const site  = this.getPersistedFilter(form, "site");
        const start = this.getPersistedFilter(form, "logs_start_date");
        const end   = this.getPersistedFilter(form, "logs_end_date");

        if (site !== undefined) this.setPersistentFilter("ai_crawler_site", site);

        if (start === undefined) {
            if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("logs_start_date", "");
        }
        if (end === undefined) {
            if (this.hasLogEndDateTarget) this.logEndDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("logs_end_date", "");
        }

        if (!this.skipDefaultDates && this.shouldInitializeDates(form)) this.ensureFilterDates({ form });

        const params = this.buildFilterParams(form);
        this.fetchAction(`${form.action || "/as_seen_by_ai/ai_crawler_reports/filter_parameters"}?${params.toString()}`);
    }

    updateFilterDate(event) {
        const input = event.target;
        const { name, value } = input;
        if (name === "logs_start_date" || name === "logs_end_date") {
            if (value) {
                this.setPersistentFilter(`ai_crawler_${name}`, value);
            } else {
                this.deletePersistentFilter(`ai_crawler_${name}`);
            }

            if (name === "logs_start_date") {
                if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((target) => { target.value = value; });
                this.syncDateInputs(name, value, input);
            }

            if (name === "logs_end_date") {
                if (this.hasLogEndDateTarget) this.logEndDateTargets.forEach((target) => { target.value = value; });
                this.syncDateInputs(name, value, input);
            }
        }
    }

    buildFilterParams(form) {
        const params = new URLSearchParams();
        const site  = this.getPersistedFilter(form, "site");
        const start = this.getPersistedFilter(form, "logs_start_date");
        const end   = this.getPersistedFilter(form, "logs_end_date");

        if (site) params.set("site", site);
        if (start !== undefined) params.set("logs_start_date", start || "");
        if (end   !== undefined) params.set("logs_end_date",   end   || "");

        return params;
    }

}
